import { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import { useWeb3React } from "@web3-react/core";
import { ethers } from "ethers";

import abiERC721 from "../constants/sourceContract/abi/abiERC721.json";
import abiERC1155 from "../constants/sourceContract/abi/abiERC1155.json";
import { formatEther } from "ethers/lib/utils";
import { getContractAbi } from "../utils";

const useContractAction = () => {
    const [show, setShow] = useState(false);
    const [stage, setStage] = useState("none");
    const [error, setError] = useState(null);
    const [action, setAction] = useState(null);

    const hideModal = () =>
        new Promise((resolve) => {
            setTimeout(() => {
                setShow(false);
                setStage("none");
                setError(null);
                resolve();
            }, 3000);
        });

    const { account, library } = useWeb3React();
    const { items } = useSelector((state) => state.contract);

    const mintToken = async (address, contractId, amount) => {
        setAction("mint-token");
        setShow(true);
        const contractItem = items.find((i) => i._id === contractId);

        let signer;
        if (library) {
            signer = await library.getSigner();
        }

        try {
            if(account && signer) {
                setStage("sign");
                const abi = getContractAbi(contractItem.type);
                const contract = new ethers.Contract(
                    contractItem.address,
                    abi,
                    signer
                );

                const gas = await contract.estimateGas.mint(address, amount)
                    .then(gas => gas)
                    .catch(() => "720000");

                let contractResponse = await contract.mint(address, amount, { gasLimit: gas }).catch((err) => {
                    return null;
                });

                if(contractResponse === null) {
                    await hideModal();
                    return { message: "User declined transaction", gasUsed: "0" };
                }

                setStage("wait");
                const tokenTx = await contractResponse.wait();

                setStage("confirm");
                const gasUsed = (
                    tokenTx.effectiveGasPrice.toNumber() *
                    parseFloat(formatEther(tokenTx.gasUsed._hex))
                ).toFixed(8);
                await hideModal();
                return { message: "Ok", gasUsed };
            }

            await hideModal();
            return { message: "ERC20: mintToken error" };
        } catch(error) {
            setError({ message: error.message });
            await hideModal();
        }
    }

    const transferERC20 = async (address, contractId, amount) => {
        setAction("transfer-token");
        setShow(true);
        const contractItem = items.find((i) => i._id === contractId);

        let signer;
        if (library) {
            signer = await library.getSigner();
        }

        try {
            if(account && signer) {
                setStage("sign");
                const abi = getContractAbi(contractItem.type);
                const contract = new ethers.Contract(
                    contractItem.address,
                    abi,
                    signer
                );

                const gas = await contract.estimateGas.transfer(address, amount)
                    .then(gas => gas)
                    .catch(() => "720000");

                let contractResponse = await contract.transfer(address, amount, { gasLimit: gas }).catch(() => {
                    return null;
                });

                if(contractResponse === null) {
                    await hideModal();
                    return { message: "User declined transaction", gasUsed: "0" };
                }

                setStage("wait");
                const tokenTx = await contractResponse.wait();

                setStage("confirm");
                const gasUsed = (
                    tokenTx.effectiveGasPrice.toNumber() *
                    parseFloat(formatEther(tokenTx.gasUsed._hex))
                ).toFixed(8);
                await hideModal();
                return { message: "Ok", gasUsed };
            }

            await hideModal();
            return { message: "ERC20: transfer-token error" };
        } catch(error) {
            setError({ message: error.message });
            await hideModal();
        }
    }

    const deleteToken = async (token, amount = 1) => {
        setAction("delete");
        setShow(true);
        const { contractId, tokenID } = token;
        const contractItem = items.find((i) => i._id === contractId);
        let signer;
        if (library) {
            signer = await library.getSigner();
        }
        try {
            if (account && signer) {
                setStage("sign");
                let abi =
                    contractItem.type === "ERC721" ? abiERC721 : abiERC1155;
                const contract = new ethers.Contract(
                    contractItem.address,
                    abi,
                    signer
                );
                let contractResponce =
                    contractItem.type === "ERC721"
                        ? await contract.burn(tokenID).catch(() => { return null })
                        : await contract.burn(tokenID, amount).catch(() => { return null });

                if(contractResponce === null) {
                    await hideModal();
                    return { message: "User declined transaction", gasUsed: 0 };
                }
                setStage("wait");
                let tokenTxn = await contractResponce.wait();
                setStage("confirm");
                const gasUsed = (
                    tokenTxn.effectiveGasPrice.toNumber() *
                    parseFloat(formatEther(tokenTxn.gasUsed._hex))
                ).toFixed(8);
                await hideModal();
                return { message: "Ok", gasUsed };
            }
            await hideModal();
            return { message: "Burning error" };
        } catch (error) {
            setError({ message: error.message });
            await hideModal();
        }
    };
    const transferToken = async (address, token, amount = 1) => {
        setAction("transfer");
        setShow(true);
        setStage("sign");
        const { contractId, tokenID } = token;
        const contractItem = items.find((i) => i._id === contractId);
        let signer;
        if (library) {
            signer = await library.getSigner();
        }
        try {
            if (account && signer) {
                let abi =
                    contractItem.type === "ERC721" ? abiERC721 : abiERC1155;
                const contract = new ethers.Contract(
                    contractItem.address,
                    abi,
                    signer
                );
                let contractResponce =
                    contractItem.type === "ERC721"
                        ? await contract.transferFrom(account, address, tokenID).catch(() => { return null })
                        : await contract.safeTransferFrom(
                              account,
                              address,
                              tokenID,
                              amount,
                              0
                          ).catch(() => { return null });
                
                if(contractResponce === null) {
                    await hideModal();
                    return { message: "User declined transaction", gasUsed: 0 };
                }
                setStage("wait");
                let tokenTxn = await contractResponce.wait();
                setStage("confirm");
                const gasUsed = (
                    tokenTxn.effectiveGasPrice.toNumber() *
                    parseFloat(formatEther(tokenTxn.gasUsed._hex))
                ).toFixed(8);
                await hideModal();
                return { message: "Ok", gasUsed };
            }
            await hideModal();
            return { message: "Transfer error" };
        } catch (error) {
            setError({ message: error.message });
            await hideModal();
        }
    };
    const transferContract = async (contractId, address) => {
        setAction("transferContract");
        setShow(true);
        setStage("sign");
        const contractItem = items.find((i) => i._id === contractId);
        let signer;
        if (library) {
            signer = await library.getSigner();
        }
        try {
            if (account && signer) {
                let abi =
                    contractItem.type === "ERC721" ? abiERC721 : abiERC1155;
                const contract = new ethers.Contract(
                    contractItem.address,
                    abi,
                    signer
                );
                const owner = await contract.owner();
                if (owner === account) {
                    let contractResponce = await contract.transferOwnership(
                        address
                    ).catch(() => { return null });

                    if(contractResponce === null) {
                        await hideModal();
                        return { message: "User declined transaction", gasUsed: 0 };
                    }

                    setStage("wait");
                    let tokenTxn = await contractResponce.wait();
                    setStage("confirm");
                    const gasUsed = (
                        tokenTxn.effectiveGasPrice.toNumber() *
                        parseFloat(formatEther(tokenTxn.gasUsed._hex))
                    ).toFixed(8);
                    await hideModal();
                    return { message: "Ok", gasUsed };
                }
                console.log("Have not permission to transfer");
            }
            await hideModal();
            return { message: "Transfer error" };
        } catch (error) {
            setError({ message: error.message });
            await hideModal();
        }
    };
    const getContractOwner = useCallback(
        async (contractId) => {
            const contractItem = items.find((i) => i._id === contractId);
            let signer;
            if (library) {
                signer = await library.getSigner();
            }
            try {
                if (account && signer) {
                    let abi =
                        contractItem.type === "ERC721" ? abiERC721 : abiERC1155;
                    const contract = new ethers.Contract(
                        contractItem.address,
                        abi,
                        signer
                    );
                    const owner = await contract.owner();
                    return { owner };
                }
            } catch (error) {
                setError({ message: error.message });
            }
        },
        [account, items, library]
    );
    return {
        deleteToken,
        transferToken,
        transferContract,
        getContractOwner,
        mintToken,
        transferERC20,
        show,
        stage,
        error,
        action,
    };
};
export { useContractAction };

// Contract
// address: "0xFdC619fE815c7a50305549eA14c0bB07e4559277"
// approve: ƒ ()
// approve(address,uint256): ƒ ()
// balanceOf: ƒ ()
// balanceOf(address): ƒ ()
// baseURI: ƒ ()
// baseURI(): ƒ ()
// burn: ƒ ()
// burn(uint256): ƒ ()
// callStatic: {approve(address,uint256): ƒ, balanceOf(address): ƒ, baseURI(): ƒ, burn(uint256): ƒ, contractURI(): ƒ, …}
// contractURI: ƒ ()
// contractURI(): ƒ ()
// estimateGas: {approve(address,uint256): ƒ, balanceOf(address): ƒ, baseURI(): ƒ, burn(uint256): ƒ, contractURI(): ƒ, …}
// filters: {Approval(address,address,uint256): ƒ, ApprovalForAll(address,address,bool): ƒ, ContractURIUpdated(string): ƒ, OwnershipTransferred(address,address): ƒ, RoyaltiesSet(uint256,(address,uint96)[]): ƒ, …}
// functions: {approve(address,uint256): ƒ, balanceOf(address): ƒ, baseURI(): ƒ, burn(uint256): ƒ, contractURI(): ƒ, …}
// getApproved: ƒ ()
// getApproved(uint256): ƒ ()
// getNextTokenId: ƒ ()
// getNextTokenId(): ƒ ()
// getRaribleV2Royalties: ƒ ()
// getRaribleV2Royalties(uint256): ƒ ()
// interface: Interface {fragments: Array(43), _abiCoder: AbiCoder, functions: {…}, errors: {…}, events: {…}, …}
// isApprovedForAll: ƒ ()
// isApprovedForAll(address,address): ƒ ()
// mint: ƒ ()
// mint(string): ƒ ()
// mintBatch: ƒ ()
// mintBatch(string[]): ƒ ()
// name: ƒ ()
// name(): ƒ ()
// owner: ƒ ()
// owner(): ƒ ()
// ownerOf: ƒ ()
// ownerOf(uint256): ƒ ()
// populateTransaction: {approve(address,uint256): ƒ, balanceOf(address): ƒ, baseURI(): ƒ, burn(uint256): ƒ, contractURI(): ƒ, …}
// provider: Web3Provider {_isProvider: true, _events: Array(0), _emitted: {…}, disableCcipRead: false, formatter: Formatter, …}
// renounceOwnership: ƒ ()
// renounceOwnership(): ƒ ()
// resolvedAddress: Promise {<fulfilled>: '0xFdC619fE815c7a50305549eA14c0bB07e4559277'}
// royalties: ƒ ()
// royalties(uint256,uint256): ƒ ()
// royaltyInfo: ƒ ()
// royaltyInfo(uint256,uint256): ƒ ()
// royaltyPercentage: ƒ ()
// royaltyPercentage(): ƒ ()
// royaltyReceiver: ƒ ()
// royaltyReceiver(): ƒ ()
// safeTransferFrom(address,address,uint256): ƒ ()
// safeTransferFrom(address,address,uint256,bytes): ƒ ()
// setApprovalForAll: ƒ ()
// setApprovalForAll(address,bool): ƒ ()
// setBaseURI: ƒ ()
// setBaseURI(string): ƒ ()
// setContractURI: ƒ ()
// setContractURI(string): ƒ ()
// setRoyalties: ƒ ()
// setRoyalties(uint256,address,uint96): ƒ ()
// setRoyaltyInfo: ƒ ()
// setRoyaltyInfo(address,uint256): ƒ ()
// signer: JsonRpcSigner {_isSigner: true, provider: Web3Provider, _index: 0, _address: null}
// supportsInterface: ƒ ()
// supportsInterface(bytes4): ƒ ()
// symbol: ƒ ()
// symbol(): ƒ ()
// tokenByIndex: ƒ ()
// tokenByIndex(uint256): ƒ ()
// tokenOfOwnerByIndex: ƒ ()
// tokenOfOwnerByIndex(address,uint256): ƒ ()
// tokenURI: ƒ ()
// tokenURI(uint256): ƒ ()
// totalSupply: ƒ ()
// totalSupply(): ƒ ()
// transferFrom: ƒ ()
// transferFrom(address,address,uint256): ƒ ()
// transferOwnership: ƒ ()
// transferOwnership(address): ƒ ()
// _runningEvents: {}
// _wrappedEmits: {}
